import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import {
  uploadFile,
  useRegisterAttachment,
  useRequestUploadUrl,
} from '@/hooks/useAttachments';

export interface PaperSignatureDialogProps {
  open: boolean;
  workOrderId: string;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (attachmentId: string) => Promise<void>;
}

export function PaperSignatureDialog({
  open,
  workOrderId,
  submitting,
  onClose,
  onSubmit,
}: PaperSignatureDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const requestUploadUrl = useRequestUploadUrl();
  const registerAttachment = useRegisterAttachment();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const busy = submitting || uploading;

  const handleClose = () => {
    if (busy) return;
    setFile(null);
    setFileError(false);
    setSubmitError(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (file == null) {
      setFileError(true);
      return;
    }
    setSubmitError(false);
    setUploading(true);
    try {
      const attachment = await uploadFile(
        file,
        [{ entityType: 'booking', entityId: workOrderId }],
        ['paper-signature'],
        t('signature.paperTitle'),
        (input) => requestUploadUrl.mutateAsync(input),
        (input) => registerAttachment.mutateAsync(input),
      );
      await onSubmit(attachment.id);
      setFile(null);
    } catch {
      setSubmitError(true);
    } finally {
      setUploading(false);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      mobileTitle={t('signature.paperTitle')}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('signature.paperTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {submitError && <Alert severity="error">{t('signature.saveFailed')}</Alert>}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            hidden
            aria-label={t('signature.paperFile')}
            onChange={(event) => {
              const next = event.target.files?.[0] ?? null;
              setFile(next);
              setFileError(next == null);
            }}
          />
          <Button variant="outlined" onClick={() => fileInputRef.current?.click()} disabled={busy}>
            {t('signature.paperFile')}
          </Button>
          {file ? <Typography variant="body2">{file.name}</Typography> : null}
          {fileError && (
            <Typography variant="caption" color="error">
              {t('signature.paperFileRequired')}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          {tc('cancel')}
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={busy}>
          {t('signature.paperSubmit')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
